// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import { space } from 'styled-system';
import Container from 'components/Container';
import { ChevronUpIcon } from 'components/ChevronIcon';
import Text from 'components/Text';
import {
  Link as VersionedRouterLink,
  NavLink as VersionedNavLink
} from 'components/RouterLink';
import TabContext from 'containers/TabContext';
import { IDEALIST_UTM_PARAMS } from 'utils/constants/general';
import { hasUTMParam, isExternalURL, pathWithParams } from 'utils/url';
import { colors, fontWeights } from '../theme';

const linkStyles = css`
  color: ${colors.waterBlue};
  font-weight: ${fontWeights.semiBold};
  word-wrap: break-word;
  &,
  &:active,
  &:visited {
    text-decoration: none;
  }
  &:hover {
    color: ${colors.niceBlue};
    text-decoration: underline;
  }
`;

const Link = styled(
  React.forwardRef<Object, any>(
    (
      { href, eventName, eventProperties, style, ...props }: Object,
      ref: any
    ) => (
      <TabContext.Consumer>
        {isTabbing => (
          <a
            ref={ref}
            href={
              isExternalURL(href) && !hasUTMParam(href)
                ? pathWithParams(href, IDEALIST_UTM_PARAMS)
                : href
            }
            {...props}
            style={isTabbing ? style : { ...style, outline: 0 }}
          />
        )}
      </TabContext.Consumer>
    )
  )
)`
  ${linkStyles}
  ${space}
`;

export default Link;

export const RouterLink = styled(VersionedRouterLink)`
  ${linkStyles}
  ${space}
`;

export const RouterNavLink = styled(VersionedNavLink)`
  ${linkStyles}
  ${space}
`;

const StyledChevronUpIcon = styled(ChevronUpIcon)`
  transform: rotate(${props => (props.open ? '180deg' : '0')});
  path,
  polyline {
    fill: currentColor;
  }
`;

const withArrow = Component => ({
  children,
  open = false,
  ...props
}: {
  children: React.Node,
  open?: boolean
}) => (
  <Component {...props}>
    {children}
    <Container as="span" pl="1em">
      <StyledChevronUpIcon open={open} />
    </Container>
  </Component>
);

export const ArrowLink = withArrow(Link);
export const ArrowRouterLink = withArrow(RouterLink);

export const LargeLink = (props: Object) => (
  <RouterLink {...props}>
    <Text
      as="span"
      textAlign={['center', 'center', 'center', 'left']}
      fontSize={['24px', '24px', '24px', '22px']}
    >
      {props.children}
    </Text>
  </RouterLink>
);

export const ConditionalLink = ({
  link,
  children,
  ...props
}: {
  link: ?string,
  children: React.Node,
  'data-qa-id'?: string
}) =>
  link ? (
    <Link data-qa-id={props['data-qa-id']} href={link} {...props}>
      {children}
    </Link>
  ) : (
    children
  );

export const ConditionalRouterLink = ({
  link,
  children,
  component,
  ...props
}: {
  link: ?string,
  children: React.Node,
  component?: React.ElementType,
  'data-qa-id'?: string
}) => {
  const Component = component || RouterLink;
  return link ? (
    <Component data-qa-id={props['data-qa-id']} to={link} {...props}>
      {children}
    </Component>
  ) : (
    children
  );
};
